import { Box, Typography } from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';
import useCallFlags from '@src/hooks/call/useCallFlags';
import useChatMessageFlags from '@src/hooks/useChatMessageFlags';
import { parseMessage } from '@src/locales/intl';
import { MediaType, MessagingProvider } from '@src/model/frontendmodel';
import SendOutlineIcon from '@src/resources/icons/SendIcon';
import { ChatEventMediaType, TimelineEvent } from '@src/types';
import { CallPermissionResponse } from '@src/types/calls';
import { ChatContact } from '@src/types/chats';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import ChatMessageContent from '../ChatMessageContent';
import ChatMessageCall from '../ChatMessageContent/ChatMessageCall';
import ChatMessageCallPermission from '../ChatMessageContent/ChatMessageCallPermission';
import ChatMessageWsCatalogueProductWrapper from '../ChatMessageContent/ChatMessageWsCatalogueProduct';
import ChatMessageAd from './ChatMessageAd';
import ChatMessageAgentButtons from './ChatMessageAgentButtons';
import ChatMessageOptions from './ChatMessageOptions';
import ChatMessageQuoted from './ChatMessageQuoted';
import ChatMessageStatus from './ChatMessageStatus';
import ListTypeMessage, { DepartmentsButton } from './ListTypeMessage';

const lblPostFooter = parseMessage('Chat.Instagram.Post.Footer');
const lblBotMsg = parseMessage('BotMessage');

export type ChatMessageProps = {
  message: TimelineEvent;
  contact: ChatContact;
};

const ChatMessage: React.FC<ChatMessageProps> = ({ message, contact }) => {
  const {
    hasTextMsg,
    notDeliveredNotifyData,
    isIncoming: isIncomingMsg,
    isOutgoing: isOutgoingMsg,
    isTemplate, // Corresponds to is-broadcast class logic (minus flow/permission)
    isAutomatic,
    isAiFollowup,
    isBot,
    isSticker,
    isQuoted,
    isQuotedFromBotButtons,
    isStartCallPermission,
    isTemplateFlow,
    isTemplateCallPermission,
    isStartTemplate,
    isResumeTemplate,
    isRequestContactInfoTemplate,
  } = useChatMessageFlags(message);

  const { isCallTerminated, isCallMissed, isCallFailed, isCallRejected } =
    useCallFlags(message?.call);

  const isCallMessage = Boolean(message?.call?.id);
  const isCallPermission = Boolean(
    message.mediaType === MediaType.PERMISSION_RESPONSE
  );

  const isCallMessageOrPermission = isCallMessage || isCallPermission;

  const hasErrorMsg = useMemo(
    () =>
      (!message?.deliveryAck && message?.deliveryFailed) ||
      !!notDeliveredNotifyData,
    [message, notDeliveredNotifyData]
  );
  /**
   * { deliveryAck: false, deliveryFailed: true } => No se entrego el mensaje Imposible!!
   * { deliveryAck: true, deliveryFailed: true } => Si se entrego !!
   * { deliveryAck: true, deliveryFailed: false } =>
   */

  const showMessageStatus = useMemo(
    () =>
      isOutgoingMsg &&
      !message.isBroadcastWapMsg &&
      !message.isAutomaticMessage &&
      !message.messageFromBot &&
      !isAiFollowup &&
      !message.call?.id &&
      !message.callPermissionResponse?.response &&
      message?.messagingProvider !== MessagingProvider.B2CBOTAPI &&
      message?.mediaType !== MediaType.TEMPLATE,
    [
      isOutgoingMsg,
      isAiFollowup,
      message.isBroadcastWapMsg,
      message.isAutomaticMessage,
      message.messageFromBot,
      message.messagingProvider,
      message.mediaType,
      message.call?.id,
      message.callPermissionResponse?.response,
    ]
  );

  const ChatMessageClass = 'ChatMessage'
    .concat(isIncomingMsg ? ' is-incoming ' : '')
    .concat(isOutgoingMsg ? ' is-outgoing ' : '')
    .concat(isCallMessageOrPermission ? 'is-call ' : '')
    .concat(isCallTerminated ? 'is-call-terminated ' : '')
    .concat(isCallMissed || isCallFailed ? 'is-call-missed ' : '')
    .concat(
      isCallRejected ||
        message?.callPermissionResponse?.response ===
          CallPermissionResponse.REJECTED
        ? 'is-call-rejected '
        : ''
    )
    .concat(isTemplate ? ' is-broadcast ' : '')
    .concat(isAutomatic ? ' is-automatic ' : '')
    .concat(isAiFollowup ? ' is-ai-followup ' : '')
    .concat(isBot ? ' is-bot ' : '')
    .concat(isSticker ? ' has-sticker' : '')
    .concat(isQuoted ? ' has-quoted' : '')
    .concat(isQuotedFromBotButtons ? ' has-quoted-bot-buttons' : '')
    .concat(isStartCallPermission ? 'is-start-call-permission' : '');

  const isInstagram = message.messagingProvider === MessagingProvider.INSTAGRAM;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-ignore
  const msgInfo =
    (isInstagram && message?.adPublicity) || isAiFollowup
      ? 'msg-info msg-info-between'
      : 'msg-info';

  let renderMessageFromBot = null;

  const isListTypeMessage = message.type === MediaType.INTERACTIVE_LIST;
  const isMessageFromBot =
    message.messageFromBot && !isStartTemplate && !isResumeTemplate;

  if (isListTypeMessage) {
    renderMessageFromBot = <ListTypeMessage message={message} />;
  } else if (isMessageFromBot && !isStartCallPermission) {
    renderMessageFromBot = (
      <Box className="ChatMessageTitle">{`🤖 ${lblBotMsg}`}</Box>
    );
  }

  const showDepartmentsButton =
    message?.messagingProvider === MessagingProvider.WHATSAPPB2CHAT &&
    isListTypeMessage;

  // Derive rendering isTemplate from hook flags
  const isRenderingTemplate =
    isTemplate &&
    !isStartTemplate &&
    !isResumeTemplate &&
    !isRequestContactInfoTemplate;

  return (
    <Box component="div" display="flex" flexDirection="column">
      <div
        className={ChatMessageClass}
        data-testid={isIncomingMsg ? 'incoming' : 'outgoing'}
      >
        {hasErrorMsg && (
          <div className="ChatMessageError">
            {notDeliveredNotifyData ? (
              <FormattedMessage id="ChatEvent.ErrorMsgNotDelivered" />
            ) : (
              <FormattedMessage id="ChatEvent.ErrorMsgDeliveryFailed" />
            )}
            <ErrorIcon style={{ width: 17, height: 17, marginLeft: 5 }} />
          </div>
        )}
        {renderMessageFromBot}
        {message.isAutomaticMessage && (
          <div className="ChatMessageTitle">
            <div style={{ marginRight: 3.5 }}>⚡</div>
            <FormattedMessage id="AutomaticMessage" />
          </div>
        )}
        {message.isAiFollowup && (
          <div className="ChatMessageTitle">
            <SendOutlineIcon
              style={{ fontSize: '1.1rem', marginRight: 3.5, color: '#7C69FF' }}
            />
            <FormattedMessage id="AiFollowup.MessageTitle" />
          </div>
        )}
        {isRenderingTemplate && (
          <div className="ChatMessageTitle">
            <>
              <div style={{ marginRight: 3.5 }}>📡</div>
              <FormattedMessage id="ChatEvent.BroadcastHeader" />
            </>
          </div>
        )}
        {isStartTemplate && (
          <div className="ChatMessageTitle">
            <div style={{ marginRight: 3.5 }}>💬</div>
            <FormattedMessage id="ChatEvent.BroadcastHeader" />
            <span style={{ margin: '0 2px' }}>-</span>
            <FormattedMessage id="ChatEvent.BroadcastHeader.Start" />
          </div>
        )}

        {isResumeTemplate && (
          <div className="ChatMessageTitle">
            <div style={{ marginRight: 3.5 }}>🛟</div>
            <FormattedMessage id="ChatEvent.BroadcastHeader" />
            <span style={{ margin: '0 2px' }}>-</span>
            <FormattedMessage id="ChatEvent.BroadcastHeader.Resume" />
          </div>
        )}

        {isRequestContactInfoTemplate && (
          <div className="ChatMessageTitle">
            <div style={{ marginRight: 3.5 }}>📡</div>
            <FormattedMessage id="ChatEvent.BroadcastHeader" />
            <span style={{ margin: '0 2px' }}>-</span>
            <FormattedMessage id="ChatEvent.BroadcastHeader.RequestContactInfo" />
          </div>
        )}

        {isTemplateFlow && (
          <div className="ChatMessageTitle">
            <div style={{ marginRight: 3.5 }}>
              🗂️ <FormattedMessage id="ChatEvent.BroadcastHeader" /> -{' '}
              <FormattedMessage id="WhatsAppFlow.chatMessage.button.default.text" />
            </div>
          </div>
        )}

        {isTemplateCallPermission && (
          <>
            <div className="ChatMessageTitle">
              <div style={{ marginRight: 3.5 }}>
                📞 <FormattedMessage id="ChatEvent.BroadcastHeader" /> -{' '}
                <FormattedMessage id="CallPermission.chatMessage.template.text" />
              </div>
            </div>
          </>
        )}

        {message?.adPublicity && (
          <ChatMessageAd
            adPayload={message.adPublicity}
            provider={message.messagingProvider}
          />
        )}
        {!!message?.quotedMessage && (
          <div style={{ marginTop: 5 }}>
            <ChatMessageQuoted
              contact={contact}
              quotedMessage={{
                ...message.quotedMessage,
                direction: message.direction,
              }}
              noShowAuthor={isQuotedFromBotButtons}
            />
          </div>
        )}
        {message?.catalogueProduct && (
          <ChatMessageWsCatalogueProductWrapper message={message} />
        )}

        {isCallMessage && <ChatMessageCall call={message.call!} />}
        {isCallPermission && message?.callPermissionResponse && (
          <ChatMessageCallPermission
            permissionResponse={message.callPermissionResponse.response}
          />
        )}

        <div
          className={'ChatMessageContent'.concat(hasTextMsg ? ' has-text' : '')}
        >
          <div
            style={{
              display: hasTextMsg ? 'inline-block' : 'flex',
              alignItems: 'center',
            }}
          >
            <ChatMessageContent
              message={message}
              isIncomingMsg={isIncomingMsg}
              isOutgoingMsg={isOutgoingMsg}
            />
            <ChatMessageOptions
              mediaType={message.mediaType as ChatEventMediaType}
              mediaUrl={message.mediaUrl}
              isIncoming={isIncomingMsg}
            />
          </div>

          <div className="ChatMessageInfo">
            <div className={msgInfo}>
              {isInstagram && message?.adPublicity && (
                <Typography>{lblPostFooter}</Typography>
              )}
              {isAiFollowup && (
                <span style={{ fontSize: '0.72rem', opacity: 0.75 }}>
                  {message.followupIndex ?? 1}/{message.followupTotal ?? 1}
                </span>
              )}
              <span style={{ marginLeft: isAiFollowup ? 'auto' : 6 }}>
                {dayjs(message?.sendingTime).format('hh:mm a')}
              </span>

              {showMessageStatus && (
                <div style={{ marginLeft: 3, display: 'flex' }}>
                  <ChatMessageStatus message={message} />
                </div>
              )}
            </div>
          </div>
        </div>
        {showDepartmentsButton && (
          <DepartmentsButton
            title={message.buttonLabel}
            departmentList={message.rows}
          />
        )}
      </div>
      {!!message?.agentChatButtons?.buttons && (
        <ChatMessageAgentButtons buttons={message?.agentChatButtons?.buttons} />
      )}
    </Box>
  );
};

export default ChatMessage;
